import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space} from "antd";
import {OfferingFormCheckoutFormValues} from "../../../../components/offering-form/general/checkout-form/types";
import {offeringCheckoutFormValidationSchema} from "../../../../components/offering-form/general/checkout-form/validation-schema";
import {useNavigate} from "react-router-dom";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {EditStoreServiceFormValues} from "../types";
import {FormWrapper} from "../../../../components/form-wrapper";
import {ProductCheckoutFormFields} from "../../../../components/offering-form/products/checkout-form";
import {OfferingDeleteButton} from "../../../../components/offering-form/general/delete-button";
import {useGetStoreServiceContext} from "../context/get-store-service";
import {useEditStoreServiceHook} from "../hooks/edit-store-service";
import {useDeleteStoreServiceHook} from "../hooks/delete-store-service";
import {IS_PWYW_ENABLED} from "../../../../services/config";
import {toSavedEditStoreServiceFormValues} from "../saved-form-values";

export function EditStoreServiceCheckoutForm() {
  const navigate = useNavigate();
  const {
    state: {storeServiceData},
  } = useGetStoreServiceContext();
  const checkoutFormMethods = useForm<OfferingFormCheckoutFormValues>({
    resolver: yupResolver(offeringCheckoutFormValidationSchema),
    mode: "onChange",
    defaultValues: {
      bookingQuestions: storeServiceData.productService.bookingQuestions
        ? storeServiceData.productService.bookingQuestions.map((q) => {
            if (
              q.answer_type === "checkbox" ||
              q.answer_type === "dropdown" ||
              q.answer_type === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: q.options.map((o) => ({
                  optionLabel: o,
                })),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
              };
            }
          })
        : [],
    },
  });
  const {editStoreService, isEditingStoreService} = useEditStoreServiceHook();
  const {deleteStoreService} = useDeleteStoreServiceHook(
    storeServiceData.productService.id
  );
  function onSubmit(values: OfferingFormCheckoutFormValues) {
    // The update replaces the whole service, so everything this tab does not edit goes
    // back exactly as saved: redirect, purchase limit, pricing model and currencies.
    const payload: EditStoreServiceFormValues = {
      ...toSavedEditStoreServiceFormValues(storeServiceData, IS_PWYW_ENABLED),
      id: storeServiceData.productService.id,
      bookingQuestions: values.bookingQuestions,
    };
    editStoreService(payload);
  }

  const isFormError = Object.keys(checkoutFormMethods.formState.errors).length > 0;
  const isBookingQuestionsError = checkoutFormMethods.formState.errors.bookingQuestions;

  return (
    <FormWrapper>
      <FormProvider {...checkoutFormMethods}>
        <form onSubmit={checkoutFormMethods.handleSubmit(onSubmit)} autoComplete="off">
          <Space size={16} direction="vertical" style={{display: "flex"}}>
            <ProductCheckoutFormFields
              isBookingQuestionsError={!!isBookingQuestionsError}
            />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}
            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    className="secondary-btn"
                    onClick={() => navigate("/products?activeTab=services")}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType="submit"
                    size="small"
                    disabled={
                      Object.keys(checkoutFormMethods.formState.errors).length > 0 ||
                      Object.values(checkoutFormMethods.watch()).length < 1
                    }
                    loading={isEditingStoreService}
                  >
                    Update service
                  </Button>
                  <OfferingDeleteButton
                    cancelText="Exit"
                    content="Are you sure you want to delete this service? This action cannot be undone."
                    okText="Delete service"
                    onOk={deleteStoreService}
                    title="Confirm service deletion"
                  />
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </FormWrapper>
  );
}
